import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { RouterModule } from '@angular/router';
import { OnboardingSetupService, SetupTask } from './onboarding-setup.service';

@Component({
  selector: 'app-onboarding-setup-bell',
  standalone: true,
  imports: [CommonModule, MatIconModule, RouterModule],
  template: `
    <div class="setup-bell-wrapper" [class.open]="isOpen" *ngIf="pendingTasksCount > 0">
      <button class="bell-btn" [class.pulse]="pendingTasksCount > 0" (click)="toggleDropdown()" type="button" title="Setup Guide">
        <mat-icon class="bell-icon">checklist</mat-icon>
        <span class="badge" *ngIf="pendingTasksCount > 0">{{ pendingTasksCount }}</span>
      </button>

      <div class="setup-panel" [class.visible]="isOpen">
        <div class="panel-header">
          <div class="panel-title">
            <mat-icon>assignment</mat-icon>
            <h3>Getting Started</h3>
          </div>
          <button class="close-btn" (click)="toggleDropdown()">
            <mat-icon>close</mat-icon>
          </button>
        </div>

        <div class="panel-content">
          <div class="task-list">
            <div class="task-item" *ngFor="let task of tasks" [class.completed]="task.isCompleted">
              <div class="task-icon">
                <mat-icon *ngIf="!task.isCompleted">{{ task.icon }}</mat-icon>
                <mat-icon *ngIf="task.isCompleted" class="success-icon">check_circle</mat-icon>
              </div>
              <div class="task-details">
                <h4>{{ task.title }}</h4>
                <p>{{ task.description }}</p>
                <a *ngIf="!task.isCompleted" [routerLink]="task.link" (click)="toggleDropdown()" class="task-action">Go to {{ task.title }}</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .setup-bell-wrapper {
      position: relative;
      display: inline-block;
    }
    .bell-btn {
      background: none;
      border: none;
      cursor: pointer;
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 8px;
      border-radius: 50%;
      color: var(--mat-sys-on-surface);
      transition: background-color 0.2s;
    }
    .bell-btn:hover {
      background-color: var(--mat-sys-surface-variant);
    }
    .bell-icon {
      font-size: 24px;
      width: 24px;
      height: 24px;
    }
    .badge {
      position: absolute;
      top: 2px;
      right: 2px;
      background-color: #f44336;
      color: white;
      font-size: 10px;
      font-weight: bold;
      min-width: 16px;
      height: 16px;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0 4px;
      border: 2px solid var(--mat-sys-surface);
    }
    .pulse {
      animation: pulse 2s infinite;
    }
    @keyframes pulse {
      0% { transform: scale(1); }
      50% { transform: scale(1.05); }
      100% { transform: scale(1); }
    }
    .setup-panel {
      position: absolute;
      top: calc(100% + 8px);
      right: 0;
      width: 320px;
      background: var(--mat-sys-surface);
      border-radius: 12px;
      box-shadow: 0 8px 24px rgba(0,0,0,0.15);
      z-index: 1000;
      opacity: 0;
      visibility: hidden;
      transform: translateY(-10px);
      transition: all 0.2s ease-in-out;
      border: 1px solid var(--mat-sys-outline-variant);
    }
    .setup-panel.visible {
      opacity: 1;
      visibility: visible;
      transform: translateY(0);
    }
    .panel-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 16px;
      border-bottom: 1px solid var(--mat-sys-outline-variant);
    }
    .panel-title {
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .panel-title h3 {
      margin: 0;
      font-size: 16px;
      font-weight: 600;
      color: var(--mat-sys-on-surface);
    }
    .panel-title mat-icon {
      color: var(--mat-sys-primary);
    }
    .close-btn {
      background: none;
      border: none;
      cursor: pointer;
      color: var(--mat-sys-on-surface-variant);
      padding: 4px;
      border-radius: 50%;
      display: flex;
    }
    .close-btn:hover {
      background-color: var(--mat-sys-surface-variant);
    }
    .panel-content {
      max-height: 400px;
      overflow-y: auto;
      padding: 16px;
    }
    .task-list {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }
    .task-item {
      display: flex;
      gap: 12px;
    }
    .task-item.completed {
      opacity: 0.6;
    }
    .task-icon {
      flex-shrink: 0;
      padding-top: 2px;
    }
    .task-icon mat-icon {
      color: var(--mat-sys-on-surface-variant);
    }
    .task-icon .success-icon {
      color: #4CAF50;
    }
    .task-details h4 {
      margin: 0 0 4px 0;
      font-size: 14px;
      font-weight: 500;
      color: var(--mat-sys-on-surface);
    }
    .task-details p {
      margin: 0 0 8px 0;
      font-size: 12px;
      color: var(--mat-sys-on-surface-variant);
      line-height: 1.4;
    }
    .task-action {
      font-size: 12px;
      font-weight: 500;
      color: var(--mat-sys-primary);
      text-decoration: none;
    }
    .task-action:hover {
      text-decoration: underline;
    }
  `]
})
export class OnboardingSetupBellComponent implements OnInit {
  private setupService = inject(OnboardingSetupService);
  
  isOpen = false;
  tasks: SetupTask[] = [];
  pendingTasksCount = 0;

  ngOnInit() {
    this.setupService.refreshTasks();
    this.setupService.tasks$.subscribe(tasks => {
      this.tasks = tasks;
    });
    this.setupService.pendingTasksCount$.subscribe(count => {
      this.pendingTasksCount = count;
      if (count === 0 && this.isOpen) {
        this.isOpen = false;
      }
    });
  }

  toggleDropdown() {
    this.isOpen = !this.isOpen;
  }
}
